import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatMessageDto, ChatRoomDto } from "src/shared/services/chat/dto";
import { getMessagePreview } from "src/shared/services/chat/attachments";

interface ChatInitialState {
  rooms: ChatRoomDto[];
  currentRoomId: string | null;
  messages: Record<string, ChatMessageDto[]>;
  isLoadingRooms: boolean;
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  searchResults: ChatMessageDto[];
  isSearching: boolean;
  offlineQueue: Array<{
    chatRoomId: string;
    message: string;
    tempId: string;
  }>;
  isOnline: boolean;
  notifications: Array<{
    roomId: string;
    message: ChatMessageDto;
    timestamp: number;
  }>;
  mutedRooms: string[];
  pinnedCaseId: string | null;
}

const initialState: ChatInitialState = {
  rooms: [],
  currentRoomId: null,
  messages: {},
  isLoadingRooms: false,
  isLoadingMessages: false,
  isSendingMessage: false,
  searchResults: [],
  isSearching: false,
  offlineQueue: [],
  isOnline: navigator.onLine,
  notifications: [],
  mutedRooms: [],
  pinnedCaseId: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setRooms: (state, action: PayloadAction<ChatRoomDto[]>) => {
      state.rooms = action.payload;
    },
    setCurrentRoomId: (state, action: PayloadAction<string | null>) => {
      state.currentRoomId = action.payload;
    },
    setMessages: (
      state,
      action: PayloadAction<{ roomId: string; messages: ChatMessageDto[] }>,
    ) => {
      state.messages[action.payload.roomId] = action.payload.messages;
    },
    addMessage: (
      state,
      action: PayloadAction<{ roomId: string; message: ChatMessageDto }>,
    ) => {
      const { roomId, message } = action.payload;
      if (!state.messages[roomId]) {
        state.messages[roomId] = [];
      }

      // Check if message already exists before adding
      const messageExists = state.messages[roomId].some(
        (m) => m.id === message.id,
      );

      if (!messageExists) {
        state.messages[roomId].push(message);
      }

      // Update room's last message
      const room = state.rooms.find((r) => r.id === roomId);
      if (room) {
        room.lastMessage = getMessagePreview(message);
        room.lastMessageDate = message.sentAt;
      }
    },
    prependMessages: (
      state,
      action: PayloadAction<{ roomId: string; messages: ChatMessageDto[] }>,
    ) => {
      const { roomId, messages } = action.payload;
      if (!state.messages[roomId]) {
        state.messages[roomId] = messages;
      } else {
        state.messages[roomId] = [...messages, ...state.messages[roomId]];
      }
    },
    removeMessage: (
      state,
      action: PayloadAction<{ roomId: string; messageId: string }>,
    ) => {
      const { roomId, messageId } = action.payload;
      if (state.messages[roomId]) {
        state.messages[roomId] = state.messages[roomId].filter(
          (m) => m.id !== messageId,
        );
      }
    },
    updateRoom: (state, action: PayloadAction<ChatRoomDto>) => {
      const index = state.rooms.findIndex((r) => r.id === action.payload.id);
      if (index !== -1) {
        state.rooms[index] = action.payload;
      } else {
        state.rooms.push(action.payload);
      }
    },
    markRoomAsRead: (state, action: PayloadAction<string>) => {
      const room = state.rooms.find((r) => r.id === action.payload);
      if (room) {
        room.unreadCount = 0;
      }

      // Mark all messages in room as read
      if (state.messages[action.payload]) {
        state.messages[action.payload] = state.messages[action.payload].map(
          (m) => ({ ...m, isRead: true }),
        );
      }
    },
    incrementUnreadCount: (state, action: PayloadAction<string>) => {
      const room = state.rooms.find((r) => r.id === action.payload);
      if (room && state.currentRoomId !== room.id) {
        room.unreadCount++;
      }
    },
    setIsLoadingRooms: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRooms = action.payload;
    },
    setIsLoadingMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMessages = action.payload;
    },
    setIsSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.isSendingMessage = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<ChatMessageDto[]>) => {
      state.searchResults = action.payload;
    },
    setIsSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    addToOfflineQueue: (
      state,
      action: PayloadAction<{
        chatRoomId: string;
        message: string;
        tempId: string;
      }>,
    ) => {
      state.offlineQueue.push(action.payload);
    },
    removeFromOfflineQueue: (state, action: PayloadAction<string>) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item) => item.tempId !== action.payload,
      );
    },
    clearOfflineQueue: (state) => {
      state.offlineQueue = [];
    },
    setIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    addNotification: (
      state,
      action: PayloadAction<{ roomId: string; message: ChatMessageDto }>,
    ) => {
      state.notifications.push({
        ...action.payload,
        timestamp: Date.now(),
      });
    },
    removeNotification: (state, action: PayloadAction<number>) => {
      state.notifications = state.notifications.filter(
        (n) => n.timestamp !== action.payload,
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    toggleMuteRoom: (state, action: PayloadAction<string>) => {
      const index = state.mutedRooms.indexOf(action.payload);
      if (index !== -1) {
        state.mutedRooms.splice(index, 1);
      } else {
        state.mutedRooms.push(action.payload);
      }
    },
    setPinnedCaseId: (state, action: PayloadAction<string | undefined>) => {
      state.pinnedCaseId = action.payload ?? null;
    },
  },
});

export const {
  setRooms,
  setCurrentRoomId,
  setMessages,
  addMessage,
  prependMessages,
  removeMessage,
  updateRoom,
  markRoomAsRead,
  incrementUnreadCount,
  setIsLoadingRooms,
  setIsLoadingMessages,
  setIsSendingMessage,
  setSearchResults,
  setIsSearching,
  addToOfflineQueue,
  removeFromOfflineQueue,
  clearOfflineQueue,
  setIsOnline,
  addNotification,
  removeNotification,
  clearNotifications,
  toggleMuteRoom,
  setPinnedCaseId,
} = chatSlice.actions;

export default chatSlice.reducer;
